import { Container, Rectangle, Sprite, type Texture } from 'pixi.js';
import { lanternFlicker } from '../../logic/realm/worlds';
import { LAMP_SIZE, STRIP_LAMPS, type Lamp } from '../../logic/realm/overview';

/** How fast a lamp comes on, per second (fully lit in about 0.7 s). */
const LIGHT_RATE = 1.4;
/** Between one lamp and the next lighting, seconds. */
const LAMP_GAP = 0.55;
/** How wide a lamp's tap area is, master pixels (the sprite is 55 wide). */
const HIT_W = 120;

export interface LampArt {
  unlit: Texture;
  lit: Texture;
  /** A soft round dot, white (the glow, and the contact shadow). */
  dot: Texture;
}

/** One lamp's state, for the tests. */
export interface LampState {
  world: number;
  side: 'west' | 'east';
  /** 0 unlit to 1 fully lit. */
  level: number;
  target: 0 | 1;
  /** Screen position of its foot is the scene's to give; this is the flicker now. */
  flame: number;
}

class LampView {
  readonly root = new Container();
  private readonly lit: Sprite;
  private readonly glow: Sprite;
  level = 0;
  target: 0 | 1 = 0;
  /** Seconds to wait before it starts to light (the moment's stagger). */
  wait = 0;
  flame = 1;

  constructor(
    readonly lamp: Lamp,
    art: LampArt,
    readonly index: number,
  ) {
    const [x, y] = lamp.foot;
    this.root.position.set(x, y);
    // The soft contact shadow under the foot.
    const shadow = new Sprite(art.dot);
    shadow.anchor.set(0.5);
    shadow.tint = 0x1a0f05;
    shadow.alpha = 0.34;
    shadow.width = 70;
    shadow.height = 22;
    const anchor = { x: 0.5, y: LAMP_SIZE.footY / LAMP_SIZE.h };
    const post = new Sprite(art.unlit);
    post.anchor.set(anchor.x, anchor.y);
    this.lit = new Sprite(art.lit);
    this.lit.anchor.set(anchor.x, anchor.y);
    this.lit.alpha = 0;
    // The glow: additive, over the lantern's head (about 15 % down the sprite).
    this.glow = new Sprite(art.dot);
    this.glow.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.glow.tint = 0xffc860;
    this.glow.width = 110;
    this.glow.height = 110;
    this.glow.alpha = 0;
    this.glow.position.set(0, -(LAMP_SIZE.footY - 36));
    this.root.addChild(shadow, post, this.lit, this.glow);
    this.root.hitArea = new Rectangle(-HIT_W / 2, -LAMP_SIZE.footY - 10, HIT_W, LAMP_SIZE.footY + 30);
    this.root.eventMode = 'static';
    this.root.cursor = 'pointer';
  }

  /** Draw its current level and flame. */
  draw(still: boolean, clock: number, count: number): void {
    this.flame = still || this.level < 1 ? 1 : lanternFlicker(this.index, count, clock);
    this.lit.alpha = this.level;
    this.glow.alpha = 0.7 * this.level * this.flame;
    this.glow.scale.set((110 / this.glow.texture.width) * (0.94 + 0.06 * this.flame));
  }
}

/**
 * The strip's fourteen lamps, two a world, on the verge either side of its clearing. Each is the unlit painted lamp with the lit one
 * faded in over it, an additive glow and a soft contact shadow; a lit lamp flickers softly (`lanternFlicker`) and stands still
 * under reduced motion. A world's pair lights together by `setLit`; `lightUp` lights worlds lamp by lamp, once. Tapping a lamp says
 * what it means (`onTap`). In master pixels, in the layer it is given.
 */
export class StripLamps {
  readonly root = new Container();
  private readonly views: LampView[];
  private clock = 0;
  private paused = false;

  constructor(
    layer: Container,
    art: LampArt,
    private readonly still: boolean,
    onTap: (world: number, lamp: Lamp) => void,
  ) {
    this.views = STRIP_LAMPS.map((l, i) => {
      const v = new LampView(l, art, i);
      v.root.on('pointertap', () => onTap(l.world, l));
      this.root.addChild(v.root);
      return v;
    });
    layer.addChild(this.root);
  }

  /** The lamps lit at once for these worlds (strip indexes), with no moment; every other world's go dark. */
  setLit(worlds: readonly number[]): void {
    const on = new Set(worlds);
    for (const v of this.views) {
      v.target = on.has(v.lamp.world) ? 1 : 0;
      v.wait = 0;
      v.level = v.target;
    }
    this.draw();
  }

  /** Light these worlds' lamps one after another, west then east, each world after the last. Returns the seconds it takes. */
  lightUp(worlds: readonly number[]): number {
    let t = 0;
    for (const w of [...worlds].sort((a, b) => a - b)) {
      for (const v of this.views.filter((x) => x.lamp.world === w)) {
        v.target = 1;
        v.wait = this.still ? 0 : t;
        if (this.still) v.level = 1;
        t += LAMP_GAP;
      }
    }
    this.draw();
    return t + 1 / LIGHT_RATE;
  }

  /** A world's lamps are lit (or lighting) or not. */
  isLit(world: number): boolean {
    return this.views.filter((v) => v.lamp.world === world).every((v) => v.target === 1);
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  /** A frame went by, `ms` long. */
  update(ms: number): void {
    if (this.paused) return;
    const dt = ms / 1000;
    this.clock += dt;
    for (const v of this.views) {
      if (v.level !== v.target) {
        if (v.wait > 0) v.wait = Math.max(0, v.wait - dt);
        else v.level = v.target > v.level ? Math.min(1, v.level + dt * LIGHT_RATE) : Math.max(0, v.level - dt * LIGHT_RATE);
      }
    }
    this.draw();
  }

  private draw(): void {
    for (const v of this.views) v.draw(this.still, this.clock, this.views.length);
  }

  /** Each lamp's state (tests). */
  states(): LampState[] {
    return this.views.map((v) => ({ world: v.lamp.world, side: v.lamp.side, level: v.level, target: v.target, flame: v.flame }));
  }

  /** A lamp's container, for its screen position (tests). */
  node(world: number, side: 'west' | 'east'): Container | null {
    return this.views.find((v) => v.lamp.world === world && v.lamp.side === side)?.root ?? null;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}
